import { FiBriefcase, FiPlus, FiChevronDown } from 'react-icons/fi';
import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Link } from 'react-router-dom';
import Logo from '../ui/Logo';
import { fixStorageUrl } from '../../utils/formatters';
import { WORKSPACE_TYPES } from '../../constants/workspaceTypes';


const WorkspaceSelector = ({ isOpen: controlledIsOpen, showAppLogo = true, compactMode = false, isMobile = false }) => {
  const { workspaces, activeWorkspace, switchWorkspace, addWorkspace, workspaceLimit, workspacesLeft } = useWorkspace();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isDropdownOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsDropdownOpen = (val) => {
      if (controlledIsOpen === undefined) setInternalIsOpen(val);
  };
  const [isAdding, setIsAdding] = useState(false);
  const [newWsName, setNewWsName] = useState('');
  const [newWsType, setNewWsType] = useState('');
  
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsAdding(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (newWsName.trim()) {
      addWorkspace(newWsName, newWsType);
      setNewWsName('');
      setNewWsType('');
      setIsAdding(false);
      setIsDropdownOpen(false);
    }
  };

  const renderLogo = (ws) => {
    const logoSrc = ws?.logo_url || (ws?.logo && ws.logo.startsWith('http') ? ws.logo : null);

    if (logoSrc) {
      return (
        <div className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden shadow-sm">
          <img src={fixStorageUrl(logoSrc)} alt={ws.name} className="w-full h-full object-cover" />
        </div>
      );
    }

    return (
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${ws?.color || 'from-charcoal-500 to-charcoal-600'} flex items-center justify-center flex-shrink-0 text-white font-bold text-xs shadow-sm`}>
        {(ws?.name || 'WS').substring(0, 2).toUpperCase()}
      </div>
    );
  };

  return (
    <div className={`relative ${!compactMode ? 'mb-6' : ''}`} ref={dropdownRef}>
       {showAppLogo && (
         <div className={`flex items-center gap-2 mb-4 px-2 ${!isDropdownOpen && 'justify-center'}`}>
            {isDropdownOpen && (
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <Logo textOnly={true} className="scale-90 origin-left" />
              </Link>
            )}
         </div>
       )}

      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
        className={`w-full flex items-center p-2 rounded-lg hover:bg-charcoal-50 dark:hover:bg-charcoal-800 transition-colors border border-transparent hover:border-charcoal-200 dark:hover:border-charcoal-700 ${
          !isDropdownOpen && !compactMode ? 'justify-center' : 'justify-between'
        }`}
      >
        <div className="flex items-center space-x-3 overflow-hidden">
          {activeWorkspace ? renderLogo(activeWorkspace) : (
             <div className="w-8 h-8 rounded-lg bg-charcoal-100 dark:bg-charcoal-700 flex items-center justify-center flex-shrink-0 text-charcoal-400 dark:text-charcoal-300">
               <SafeIcon icon={FiBriefcase} />
            </div>
          )}

          {(isDropdownOpen || compactMode) && (
            <div className="text-left overflow-hidden">
              <h2 className="text-sm font-bold text-charcoal-800 dark:text-charcoal-200 truncate">{activeWorkspace?.name || 'No Workspace'}</h2>
              {activeWorkspace ? (
                <p className="text-xs text-charcoal-500 dark:text-charcoal-400 truncate">{activeWorkspace.type || 'Workspace'}</p>
              ) : (
                <p className="text-xs text-charcoal-500 dark:text-charcoal-400 truncate">Create New</p>
              )}
            </div>
          )}
        </div>
        {(isDropdownOpen || compactMode) && <SafeIcon icon={FiChevronDown} className={`text-charcoal-400 dark:text-charcoal-500 text-sm flex-shrink-0 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />}
      </button>

      {isDropdownOpen && (
        <div className={isMobile
          ? "w-full mt-2 bg-white dark:bg-charcoal-800 rounded-xl border border-charcoal-100 dark:border-charcoal-700 overflow-hidden animate-fade-in"
          : "absolute top-full left-0 w-64 mt-2 bg-white dark:bg-charcoal-800 rounded-xl shadow-xl border border-charcoal-100 dark:border-charcoal-700 z-50 overflow-hidden animate-fade-in"
        }>
          <div className="p-3 border-b border-charcoal-100 dark:border-charcoal-700 bg-charcoal-50 dark:bg-charcoal-800/50">
            <p className="text-xs font-semibold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider">Switch Workspace</p>
          </div>
          
          <div className="max-h-64 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-charcoal-200 dark:scrollbar-thumb-charcoal-700">
            {workspaces
              .filter(ws => String(ws.id) !== String(activeWorkspace?.id))
              .map((ws) => (
              <button
                key={ws.id}
                onClick={() => {
                  switchWorkspace(ws.id);
                  setIsDropdownOpen(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2 hover:bg-charcoal-50 dark:hover:bg-charcoal-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {renderLogo(ws)}
                  <div className="text-left">
                    <p className="text-sm font-medium text-charcoal-600 dark:text-charcoal-400 hover:text-charcoal-900 dark:hover:text-white">
                      {ws.name}
                    </p>
                    <p className="text-xs text-charcoal-400 dark:text-charcoal-500">{ws.type}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="border-t border-charcoal-100 dark:border-charcoal-700 p-2">
            {isAdding ? (
              <form onSubmit={handleAddSubmit} className="p-2">
                <div className="mb-3 text-xs text-charcoal-500 dark:text-charcoal-400 bg-charcoal-50 dark:bg-charcoal-800 p-2 rounded">
                  <p className="font-medium mb-1">🌱 Organize with Workspaces</p>
                  <p>All workspaces share your monthly Garden Seeds.</p>
                </div>
                <label htmlFor="new-workspace-name" className="sr-only">Workspace Name</label>
                <input
                  type="text"
                  value={newWsType}
                  onChange={(e) => setNewWsType(e.target.value)}
                  list="workspace-selector-types"
                  placeholder="e.g. B2B - SaaS, MLM Distributor"
                  className="w-full px-3 py-2 text-sm border border-charcoal-300 dark:border-charcoal-600 dark:bg-charcoal-700 dark:text-white rounded-md focus:ring-2 focus:ring-charcoal-500 focus:border-transparent mb-2"
                />
                <datalist id="workspace-selector-types">
                    {WORKSPACE_TYPES.map(type => (
                        <option key={type} value={type} />
                    ))}
                    <option value="Personal" />
                </datalist>
                <input
                  id="new-workspace-name"
                  type="text"
                  value={newWsName}
                  onChange={(e) => setNewWsName(e.target.value)}
                  placeholder="Workspace Name"
                  aria-label="Workspace Name"
                  className="w-full px-3 py-2 text-sm border border-charcoal-300 dark:border-charcoal-600 dark:bg-charcoal-700 dark:text-white rounded-md focus:ring-2 focus:ring-charcoal-500 focus:border-transparent mb-2"
                  autoFocus
                />
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-charcoal-900 dark:bg-charcoal-600 text-white text-xs py-1.5 rounded-md hover:bg-charcoal-800 dark:hover:bg-charcoal-500"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAdding(false)}
                    className="flex-1 bg-charcoal-100 dark:bg-charcoal-700 text-charcoal-600 dark:text-charcoal-300 text-xs py-1.5 rounded-md hover:bg-charcoal-200 dark:hover:bg-charcoal-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setIsAdding(true)}
                className="w-full flex items-center justify-between px-4 py-2 text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-700 rounded-lg transition-colors group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg border-2 border-dashed border-charcoal-300 dark:border-charcoal-600 flex items-center justify-center group-hover:border-charcoal-400 dark:group-hover:border-charcoal-500 transition-colors">
                    <SafeIcon icon={FiPlus} className="text-charcoal-400 dark:text-charcoal-500 group-hover:text-charcoal-600 dark:group-hover:text-charcoal-400 transition-colors" />
                  </div>
                  <div className="text-left">
                     <span className="text-sm font-medium block">Create Workspace</span>
                     <span className="text-xs text-charcoal-400 dark:text-charcoal-500">
                       {workspaces.length} active (Unlimited)
                     </span>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSelector;
